/**
 * NF525 — Daily Z closure reminder
 *
 * Checks whether the tenant has performed a Z closure in the last 24h.
 * Surfaces a banner on /caisse + schedules an evening toast.
 *
 * Fiscal context (art. 286 CGI + LF 2018):
 * - Daily Z closure is MANDATORY for each day with sales activity.
 * - Forgetting does NOT corrupt data (tickets are individually sealed),
 *   but creates a procedural anomaly that is flagged in any DGFIP audit.
 * - A catch-up Z today aggregates all sales since the last Z — hash chain
 *   stays valid, but the JET logs an anomaly (>24h since last closure).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffectiveTenantId } from './useTenant';

export interface ZClosureReminderStatus {
  /** ISO date of the last Z closure (or null if none ever performed) */
  lastZAt: string | null;
  /** Hours since the last Z closure (Infinity if never) */
  hoursSinceLastZ: number;
  /** Count of unclosed sales since last Z (activity gate) */
  unclosedSalesCount: number;
  /** true if > 24h AND there are unclosed sales */
  isOverdue: boolean;
  /** Approx number of full days of activity without Z */
  overdueDays: number;
}

/**
 * Query the last Z closure + count of sales since. Refetches every 5 min.
 */
export function useZClosureReminder() {
  const tenantId = useEffectiveTenantId();

  return useQuery<ZClosureReminderStatus>({
    queryKey: ['z_closure_reminder', tenantId],
    queryFn: async () => {
      if (!tenantId) {
        return {
          lastZAt: null,
          hoursSinceLastZ: Infinity,
          unclosedSalesCount: 0,
          isOverdue: false,
          overdueDays: 0,
        };
      }

      // 1. Last Z closure
      const { data: lastZ } = await supabase
        .from('z_closures')
        .select('period_end, created_at')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const lastZAt = (lastZ as any)?.period_end || (lastZ as any)?.created_at || null;
      const now = Date.now();
      const hoursSinceLastZ = lastZAt
        ? (now - new Date(lastZAt).getTime()) / (1000 * 60 * 60)
        : Infinity;

      // 2. Count sales since the last Z (activity check). If no Z ever,
      //    count all fiscal sales.
      // "Fiscal" sales = completed sales (is_fiscal column doesn't exist on
      // this schema — status='completed' is the non-cancelled non-refunded
      // equivalent that feeds the Z closure totals).
      let salesQuery = supabase
        .from('sales')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .eq('status', 'completed');

      if (lastZAt) salesQuery = salesQuery.gt('created_at', lastZAt);

      const { count } = await salesQuery;
      const unclosedSalesCount = count || 0;

      // Threshold raised from 24h to 48h so fresh installs (no Z ever) and
      // users who just missed yesterday's closure don't see the reminder on
      // every app open. The fiscal obligation is still daily, but the banner
      // is UX — the real audit trail is in fiscal_events / JET.
      const isOverdue = hoursSinceLastZ > 48 && unclosedSalesCount > 0;
      const overdueDays =
        isOverdue && isFinite(hoursSinceLastZ)
          ? Math.floor(hoursSinceLastZ / 24)
          : isOverdue
            ? 99
            : 0; // "never closed" reads as 99 days for severity math

      return { lastZAt, hoursSinceLastZ, unclosedSalesCount, isOverdue, overdueDays };
    },
    enabled: !!tenantId,
    refetchInterval: 5 * 60 * 1000, // every 5 min
    staleTime: 60 * 1000,
  });
}
